import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { useAppStore } from '@/lib/store';
import { Check, ChevronRight, Globe, Banknote, Zap, HelpCircle, AlertCircle, Shield, FileText, BookOpen, Info } from 'lucide-react';
import Colors from '@/constants/colors';
import { getTranslation } from '@/constants/translations';
import type { Language, Currency } from '@/types/index';
import PageContainer from '@/components/PageContainer';
import PageHeader from '@/components/PageHeader';
import ContentSection from '@/components/ContentSection';

const languages: { code: Language; name: string; nativeName: string }[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
];

const currencies: { code: Currency; name: string; symbol: string }[] = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
];

export default function SettingsPage() {
  const { language, setLanguage } = useAppLanguage();
  const currency = useAppStore((state) => state.currency);
  const setCurrency = useAppStore((state) => state.setCurrency);

  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);

  const selectedLanguage = languages.find((l) => l.code === language);
  const selectedCurrency = currencies.find((c) => c.code === currency);

  return (
    <PageContainer>
      <PageHeader title={getTranslation('settings', language)} />

      {/* App Settings Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>{getTranslation('appSettings', language) || 'App Settings'}</h2>
        <div style={styles.contentWrapper}>
          <ContentSection style={styles.groupContainer}>
          {/* Language Button */}
          <button
            style={styles.groupItem}
            onClick={() => setShowLanguageModal(true)}
          >
            <div style={styles.iconContainer}>
              <Globe size={20} color={Colors.primary} />
            </div>
            <div style={styles.itemLabel}>
              <span style={styles.itemTitle}>{getTranslation('language', language)}</span>
              <span style={styles.itemSubtitle}>{selectedLanguage?.nativeName}</span>
            </div>
            <ChevronRight size={20} color={Colors.textSecondary} />
          </button>

          {/* Currency Button */}
          <button
            style={{ ...styles.groupItem, borderTop: `1px solid ${Colors.border}` }}
            onClick={() => setShowCurrencyModal(true)}
          >
            <div style={styles.iconContainer}>
              <Banknote size={20} color={Colors.primary} />
            </div>
            <div style={styles.itemLabel}>
              <span style={styles.itemTitle}>{getTranslation('currency', language)}</span>
              <span style={styles.itemSubtitle}>{selectedCurrency?.symbol} - {selectedCurrency?.code}</span>
            </div>
            <ChevronRight size={20} color={Colors.textSecondary} />
          </button>

          {/* Manage Widgets Button */}
          <Link
            to="/settings/widgets"
            style={{ textDecoration: 'none' }}
          >
            <button style={{ ...styles.groupItem, borderTop: `1px solid ${Colors.border}` }}>
              <div style={styles.iconContainer}>
                <Zap size={20} color={Colors.primary} />
              </div>
              <div style={styles.itemLabel}>
                <span style={styles.itemTitle}>{getTranslation('manageWidgets', language) || 'Manage Widgets'}</span>
              </div>
              <ChevronRight size={20} color={Colors.textSecondary} />
            </button>
          </Link>
          </ContentSection>
        </div>
      </div>

      {/* Help Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>{getTranslation('help', language) || 'Help'}</h2>
        <div style={styles.contentWrapper}>
          <ContentSection style={styles.groupContainer}>
          {/* Help Button */}
          <Link
            to="/settings/help"
            style={{ textDecoration: 'none' }}
          >
            <button style={styles.groupItem}>
              <div style={styles.iconContainer}>
                <HelpCircle size={20} color={Colors.primary} />
              </div>
              <div style={styles.itemLabel}>
                <span style={styles.itemTitle}>{getTranslation('help', language) || 'Help'}</span>
              </div>
              <ChevronRight size={20} color={Colors.textSecondary} />
            </button>
          </Link>

          {/* About Umland Button */}
          <Link
            to="/settings/about"
            style={{ textDecoration: 'none' }}
          >
            <button style={{ ...styles.groupItem, borderTop: `1px solid ${Colors.border}` }}>
              <div style={styles.iconContainer}>
                <Info size={20} color={Colors.primary} />
              </div>
              <div style={styles.itemLabel}>
                <span style={styles.itemTitle}>{getTranslation('aboutUmland', language) || 'About Umland'}</span>
              </div>
              <ChevronRight size={20} color={Colors.textSecondary} />
            </button>
          </Link>

          {/* Report Issue Button */}
          <Link
            to="/settings/report-issue"
            style={{ textDecoration: 'none' }}
          >
            <button style={{ ...styles.groupItem, borderTop: `1px solid ${Colors.border}` }}>
              <div style={styles.iconContainer}>
                <AlertCircle size={20} color={Colors.primary} />
              </div>
              <div style={styles.itemLabel}>
                <span style={styles.itemTitle}>{getTranslation('reportIssue', language) || 'Report an Issue'}</span>
              </div>
              <ChevronRight size={20} color={Colors.textSecondary} />
            </button>
          </Link>
          </ContentSection>
        </div>
      </div>

      {/* Other Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>{getTranslation('other', language) || 'Other'}</h2>
        <div style={styles.contentWrapper}>
          <ContentSection style={styles.groupContainer}>
          {/* Privacy Policy Button */}
          <Link
            to="/settings/privacy-policy"
            style={{ textDecoration: 'none' }}
          >
            <button style={styles.groupItem}>
              <div style={styles.iconContainer}>
                <Shield size={20} color={Colors.primary} />
              </div>
              <div style={styles.itemLabel}>
                <span style={styles.itemTitle}>{getTranslation('privacyPolicy', language) || 'Privacy Policy'}</span>
              </div>
              <ChevronRight size={20} color={Colors.textSecondary} />
            </button>
          </Link>

          {/* Terms of Service Button */}
          <Link
            to="/settings/terms-of-service"
            style={{ textDecoration: 'none' }}
          >
            <button style={{ ...styles.groupItem, borderTop: `1px solid ${Colors.border}` }}>
              <div style={styles.iconContainer}>
                <FileText size={20} color={Colors.primary} />
              </div>
              <div style={styles.itemLabel}>
                <span style={styles.itemTitle}>{getTranslation('termsOfService', language) || 'Terms of Service'}</span>
              </div>
              <ChevronRight size={20} color={Colors.textSecondary} />
            </button>
          </Link>

          {/* Changelog Button */}
          <Link
            to="/settings/changelog"
            style={{ textDecoration: 'none' }}
          >
            <button style={{ ...styles.groupItem, borderTop: `1px solid ${Colors.border}` }}>
              <div style={styles.iconContainer}>
                <BookOpen size={20} color={Colors.primary} />
              </div>
              <div style={styles.itemLabel}>
                <span style={styles.itemTitle}>{getTranslation('changelog', language) || 'Changelog'}</span>
              </div>
              <ChevronRight size={20} color={Colors.textSecondary} />
            </button>
          </Link>
          </ContentSection>
        </div>
      </div>

      {/* Language Modal */}
      {showLanguageModal && (
        <div style={styles.modalOverlay} onClick={() => setShowLanguageModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>{getTranslation('selectLanguage', language) || 'Select Language'}</h3>
            {languages.map((lang) => (
              <button
                key={lang.code}
                style={styles.modalItem}
                onClick={() => {
                  setLanguage(lang.code);
                  setShowLanguageModal(false);
                }}
              >
                <span style={styles.modalItemText}>{lang.nativeName}</span>
                {language === lang.code && <Check size={20} color={Colors.primary} />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Currency Modal */}
      {showCurrencyModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCurrencyModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>{getTranslation('selectCurrency', language) || 'Select Currency'}</h3>
            {currencies.map((curr) => (
              <button
                key={curr.code}
                style={styles.modalItem}
                onClick={() => {
                  setCurrency(curr.code);
                  setShowCurrencyModal(false);
                }}
              >
                <span style={styles.modalItemText}>{curr.symbol} - {curr.name}</span>
                {currency === curr.code && <Check size={20} color={Colors.primary} />}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ height: '40px' }} />
    </PageContainer>
  );
}

const styles = {
  section: {
    marginBottom: '32px',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: Colors.textSecondary,
    paddingLeft: '16px',
    paddingRight: '16px',
    marginBottom: '16px',
    margin: 0,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  } as React.CSSProperties,

  contentWrapper: {
    paddingLeft: '16px',
    paddingRight: '16px',
  } as React.CSSProperties,

  groupContainer: {
    backgroundColor: Colors.surface,
    borderRadius: '12px',
    overflow: 'hidden',
    border: `1px solid ${Colors.border}`,
  } as React.CSSProperties,

  groupItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: Colors.surface,
    border: 'none',
    padding: '12px 16px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    width: '100%',
    boxSizing: 'border-box' as const,
    minHeight: '52px',
    gap: '12px',
  } as React.CSSProperties,

  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '28px',
    height: '28px',
  } as React.CSSProperties,

  itemLabel: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    gap: '2px',
    flex: 1,
  } as React.CSSProperties,

  itemTitle: {
    fontSize: '15px',
    fontWeight: '500',
    color: Colors.text,
    lineHeight: '1.2',
  } as React.CSSProperties,

  itemSubtitle: {
    fontSize: '12px',
    color: Colors.textSecondary,
    lineHeight: '1.2',
  } as React.CSSProperties,

  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  } as React.CSSProperties,

  modalContent: {
    backgroundColor: Colors.surface,
    borderRadius: '16px',
    padding: '20px',
    width: 'calc(100% - 32px)',
    maxWidth: '400px',
    maxHeight: '80vh',
    overflowY: 'auto' as const,
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,

  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: Colors.text,
    marginBottom: '16px',
    margin: 0,
  } as React.CSSProperties,

  modalItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: Colors.surface,
    border: `1px solid ${Colors.border}`,
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    width: '100%',
  } as React.CSSProperties,

  modalItemText: {
    fontSize: '16px',
    color: Colors.text,
  } as React.CSSProperties,
};
